import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, Pressable, StyleSheet, TextInput } from 'react-native';
import ColorPicker from 'react-native-wheel-color-picker';

import { Coordinate, RouteType } from 'types/map';
import { dataIsValid } from 'utils/helpers';
import { EditCoordinate } from './EditCoordinate';
import { useAppDispatch, useAppSelector } from 'hooks/hooks';
import { change } from 'hooks/routesSlice';
import { initEdit, stopEdit } from 'hooks/editSlice';

function RouteRow({
  route,
  styles,
  deleteRoute,
  save,
  cancelChanges,
  hasChanged,
}: {
  route: RouteType;
  styles: StyleSheet.NamedStyles<any>;
  deleteRoute: (route: RouteType) => void;
  save: (route: RouteType) => void;
  cancelChanges: (route: RouteType) => void;
  hasChanged: (route: RouteType) => boolean;
}) {
  const dispatch = useAppDispatch();
  const edit = useAppSelector((state) => state.edit);
  const form = {
    name: {
      required: true,
      errorMessage: 'Name is required.',
      error: null,
    },
    routes: {
      required: true,
      errorMessage: 'Route is required.',
      error: null,
    },
  };
  const [isChangingCoords, setIsChangingCoords] = useState(false);
  const [formValidation, setFormValidation] = useState({ ...form });

  const onDataChange = useCallback(
    (key: string, value: string | Coordinate[]) => {
      if (value !== route[key]) {
        dispatch(
          change({
            route: {
              ...route,
              [key]: value,
            },
          }),
        );
      }
    },
    [dispatch, route],
  );

  useEffect(() => {
    if (!route.routes && !isChangingCoords) {
      setIsChangingCoords(true);
    }

    if (isChangingCoords && edit.value.coords?.length) {
      onDataChange('routes', edit.value.coords);
    }
  }, [edit, route, onDataChange, isChangingCoords, dispatch]);

  return (
    <View style={styles.row}>
      <Text style={styles.label}>Title:</Text>
      <TextInput
        value={route.name}
        style={[
          styles.input,
          formValidation.name.error ? styles.inputError : null,
        ]}
        onChangeText={(val) => onDataChange('name', val)}
      />
      {formValidation.name.error && (
        <Text style={styles.error}>{formValidation.name.errorMessage}</Text>
      )}

      <View style={style.colorPicker}>
        <Text style={styles.label}>Route Color:</Text>
        <ColorPicker
          color={route.color}
          onColorChangeComplete={(color) => onDataChange('color', color)}
          thumbSize={30}
          sliderSize={20}
          noSnap={true}
          swatches={false}
        />
      </View>

      <View>
        <EditCoordinate
          coord={route.routes}
          onClear={() => {
            onDataChange('routes', null);
          }}
        />
        {!route.routes && <Text style={styles.action}>Select area on map</Text>}
        {formValidation.routes.error && (
          <Text style={styles.error}>{formValidation.routes.errorMessage}</Text>
        )}
      </View>

      <View style={styles.inlineButtonContainer}>
        <Pressable onPress={() => deleteRoute(route)}>
          <Text style={[styles.inlineButton, styles.error]}>Delete</Text>
        </Pressable>
        {hasChanged(route) && (
          <Pressable
            onPress={() => {
              cancelChanges(route);
              dispatch(stopEdit());
              setIsChangingCoords(false);
            }}
          >
            <Text style={styles.inlineButton}>Cancel</Text>
          </Pressable>
        )}
        {hasChanged(route) && (
          <Pressable
            onPress={() => {
              if (dataIsValid(formValidation, setFormValidation, route)) {
                save(route);
                dispatch(stopEdit());
                setIsChangingCoords(false);
              } else {
                console.log('Data not valid');
              }
            }}
          >
            <Text style={[styles.inlineButton, styles.success]}>Save</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

export function EditRoutes({
  styles,
  addRoute,
  deleteRoute,
  saveRoute,
  displayError,
  cancelChanges,
  hasChanged,
}: {
  styles: StyleSheet.NamedStyles<any>;
  addRoute: () => void;
  deleteRoute: (route: RouteType) => void;
  saveRoute: (route: RouteType) => Promise<void>;
  displayError: (msg: string) => void;
  cancelChanges: (route: RouteType) => void;
  hasChanged: (route: RouteType) => boolean;
}) {
  const dispatch = useAppDispatch();
  const routes = useAppSelector((state) => state.routes);
  function save(route: RouteType) {
    const clone = [...routes.value];
    clone.forEach((d, index) => {
      if (d.id === route.id) {
        clone[index] = route;
        return false;
      }
    });

    console.log('going to save', route);

    return saveRoute(route).catch((err) => {
      console.log('Save route error:', err);
      displayError('Route not saved, please try again');
    });
  }

  return (
    <View>
      {routes &&
        routes.value.map((route) => (
          <RouteRow
            key={route.id}
            route={route}
            styles={styles}
            deleteRoute={deleteRoute}
            save={save}
            cancelChanges={cancelChanges}
            hasChanged={hasChanged}
          />
        ))}
      {addRoute && (
        <View style={styles.buttonContainer}>
          <Pressable
            onPress={() => {
              addRoute();
              dispatch(initEdit());
            }}
            style={styles.button}
          >
            <Text style={styles.buttonText}>Add Route</Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}

const style = StyleSheet.create({
  coords: {
    flexDirection: 'row',
  },
  colorPicker: {
    paddingTop: 10,
    paddingBottom: 20,
  },
});
